<template>
  <main class="bg-[#fffffc] min-h-screen">
    <section class="">
      <div class="flex flex-col items-center justify-center px-6 py-16 mx-auto">
        <div class="w-full border border-gray-200 shadow-sm md:mt-0 sm:max-w-xl bg-white">
          <div class="p-6 space-y-4 md:space-y-6 sm:p-8">
            <div class="flex justify-center">
              <img
                alt="Nevcap Logo"
                class="h-10"
                src="../../assets/images/nevcap-logo.svg"
              >
            </div>
            <h1 class="text-xl font-semibold leading-tight tracking-tight text-gray-900 md:text-2xl">
              Sign in
            </h1>
            <form
              class="space-y-4 md:space-y-6"
              @submit.prevent="login"
            >
              <div>
                <label
                  class="block mb-2 text-sm font-medium text-gray-900"
                  for="email"
                >Email</label>
                <input
                  id="email"
                  v-model="email"
                  class="bg-white border border-gray-300 text-gray-900 sm:text-sm focus:ring-primary focus:border-primary block w-full p-2.5"
                  name="email"
                  placeholder="you@company.com"
                  required
                  type="email"
                >
              </div>
              <div>
                <label
                  class="block mb-2 text-sm font-medium text-gray-900"
                  for="password"
                >Password</label>
                <input
                  id="password"
                  v-model="password"
                  class="bg-white border border-gray-300 text-gray-900 sm:text-sm focus:ring-primary focus:border-primary block w-full p-2.5"
                  name="password"
                  placeholder="••••••••"
                  required
                  type="password"
                >
              </div>
              <div
                class="flex items-center justify-between"
                style="display: none;"
              >
                <div class="flex items-start">
                  <div class="flex items-center h-5">
                    <input
                      id="remember"
                      aria-describedby="remember"
                      class="w-4 h-4 border border-pale-creamrounded bg-gray-50 focus:ring-3 focus:ring-primary-300 dark:bg-gray-700 dark:border-gray-600 dark:focus:ring-primary-600 dark:ring-offset-gray-800"
                      type="checkbox"
                    >
                  </div>
                  <div class="ml-3 text-sm">
                    <label
                      class="text-gray-500 dark:text-gray-300"
                      for="remember"
                    >Remember me</label>
                  </div>
                </div>
                <a
                  class="text-sm font-medium text-primary hover:underline"
                  href="#"
                >Forgot your password?</a>
              </div>
              <button
                class="w-full inline-flex justify-center items-center py-3 px-5 text-base font-medium text-center text-white bg-primary hover:bg-gray-700"
                type="submit"
              >
                Sign in
                <svg
                  class="ml-2 -mr-1 w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    clip-rule="evenodd"
                    d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                    fill-rule="evenodd"
                  />
                </svg>
              </button>
              <div class="flex justify-center">
                <div ref="googleButtonRef" />
              </div>
              <p class="text-sm font-light text-gray-500">
                Don't have an account?
                <router-link
                  class="font-medium text-primary hover:underline"
                  to="/register"
                >
                  Register
                </router-link>
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script lang="ts">
import {defineComponent, inject, onMounted, ref, watchEffect} from 'vue';
import axios, {AxiosError} from 'axios';
import {useRouter} from 'vue-router';
import {userKey} from '@/composables/useUser';
import {settingsKey} from "@/composables/useSettings";
import {useHead} from "@vueuse/head";

export default defineComponent({
  name: 'Login',
  setup() {
    const router = useRouter();
    const email = ref('');
    const password = ref('');
    const errorMessage = ref<string | null>(null);
    const googleButtonRef = ref<HTMLDivElement | null>(null);

    const {user} = inject(userKey)!;

    const {settings} = inject(settingsKey)!;

    watchEffect(() => {
      if (settings.value) {
        useHead({
          title: 'Login',
          meta: [
            {name: 'description', content: '...'},
            {name: 'keywords', content: '...'},
            {name: 'author', content: '...'},
          ],
        });
      }
    });

    const login = async () => {
      try {
        const response = await axios.post('/auth/login', {
          email: email.value,
          password: password.value,
        });

        user.value = response.data.user;

        await router.push('/dashboard');
      } catch (error: unknown) {
        const axiosError = error as AxiosError<{ message: string }>;

        if (axiosError.response) {
          errorMessage.value = axiosError.response.data?.message || 'Login failed.';
        } else if (axiosError.request) {
          errorMessage.value = 'Could not reach the server. Please check your internet connection.';
        } else {
          errorMessage.value = 'An unknown error occurred.';
        }

        console.error('Login error:', errorMessage.value);
      }
    };

    const loadGoogleScript = (): Promise<void> => {
      return new Promise((resolve, reject) => {
        if (document.getElementById('google-identity-script')) {
          return resolve();
        }
        const script = document.createElement('script');
        script.id = 'google-identity-script';
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Google Identity script'));
        document.head.appendChild(script);
      });
    };

    onMounted(async () => {
      const clientId = process.env.VUE_APP_GOOGLE_CLIENT_ID;
      if (!clientId || !googleButtonRef.value) return;

      try {
        await loadGoogleScript();
        const google = (window as any).google;
        if (!google?.accounts?.id) return;

        google.accounts.id.initialize({
          client_id: clientId,
          callback: async (response: { credential: string }) => {
            try {
              const res = await axios.post('/auth/google', {
                credential: response.credential,
              });
              user.value = res.data.user;
              await router.push('/dashboard');
            } catch (err) {
              console.error('Google login error:', err);
              errorMessage.value = 'Google sign-in failed.';
            }
          },
        });

        google.accounts.id.renderButton(googleButtonRef.value, {
          theme: 'outline',
          size: 'large',
          width: 320,
        });
      } catch (err) {
        console.error(err);
      }
    });

    return {settings, email, password, login, errorMessage, googleButtonRef};
  },
});
</script>
